import { useState, useRef, useCallback } from 'react';

// ─── ImageUploader ────────────────────────────────────────────────────────────
// Props:
//   images       — File[] currently selected
//   onImagesChange — (File[]) => void
//   maxImages    — number (default 10)
//   minImages    — number (default 1)
export default function ImageUploader({
  images = [],
  onImagesChange,
  maxImages = 10,
  minImages = 1,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  const canAddMore = images.length < maxImages;

  // ── Validate and add files
  const addFiles = useCallback(
    (incomingFiles) => {
      setValidationError(null);

      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      const maxSizeBytes = 20 * 1024 * 1024; // 20MB per image
      const validated = [];
      const errors = [];

      for (const file of incomingFiles) {
        if (!validTypes.includes(file.type) && !file.name.match(/\.(heic|heif)$/i)) {
          errors.push(`${file.name}: unsupported format`);
          continue;
        }
        if (file.size > maxSizeBytes) {
          errors.push(`${file.name}: too large (max 20MB)`);
          continue;
        }
        validated.push(file);
      }

      if (errors.length) {
        setValidationError(errors[0]);
      }

      // Merge with existing, cap at maxImages
      const merged = [...images, ...validated].slice(0, maxImages);
      onImagesChange(merged);
    },
    [images, maxImages, onImagesChange]
  );

  // ── Input change handler
  const handleInputChange = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length) addFiles(files);
      // Reset so the same file can be re-selected
      e.target.value = '';
    },
    [addFiles]
  );

  // ── Drag handlers (for desktop testing)
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only leave if we're actually leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files || []).filter((f) =>
        f.type.startsWith('image/')
      );
      if (files.length) addFiles(files);
    },
    [addFiles]
  );

  // ── Remove image
  const removeImage = useCallback(
    (indexToRemove) => {
      const next = images.filter((_, i) => i !== indexToRemove);
      onImagesChange(next);
    },
    [images, onImagesChange]
  );

  // ── Reorder: move a photo left
  const moveLeft = useCallback(
    (index) => {
      if (index === 0) return;
      const next = [...images];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      onImagesChange(next);
    },
    [images, onImagesChange]
  );

  // ── Open file picker
  const openPicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // ── Progress indicator
  const filledSlots = images.length;
  const isReady = filledSlots >= minImages;

  return (
    <div className="uploader">
      {/* ── Hidden file input — accepts camera roll + HEIC */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,image/heic,image/heif"
        multiple
        capture={false}       // false = camera roll preferred, not direct camera
        onChange={handleInputChange}
        aria-label="Select photos from your camera roll"
        style={{ display: 'none' }}
      />

      {/* ── Empty State / Drop Zone (shown when no images yet) */}
      {images.length === 0 && (
        <div
          className={`drop-zone ${isDragging ? 'drop-zone--dragging' : ''}`}
          onClick={openPicker}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          aria-label="Tap to select photos from your camera roll"
          onKeyDown={(e) => e.key === 'Enter' || e.key === ' ' ? openPicker() : null}
        >
          <div className="drop-zone__inner">
            <div className="drop-zone__icon-wrap">
              <span className="drop-zone__icon" aria-hidden="true">📷</span>
              <div className="drop-zone__icon-ring" aria-hidden="true" />
            </div>
            <p className="drop-zone__title">Add Photos</p>
            <p className="drop-zone__subtitle">
              Tap to choose from your camera roll
            </p>
            <div className="drop-zone__format-hint">
              JPG · PNG · HEIC · WEBP · up to 20MB each
            </div>
          </div>
        </div>
      )}

      {/* ── Photo Grid (shown when images are selected) */}
      {images.length > 0 && (
        <div className="photo-section">
          {/* Slot counter */}
          <div className="slot-counter" aria-live="polite">
            <span className="slot-counter__dots" aria-hidden="true">
              {Array.from({ length: maxImages }).map((_, i) => (
                <span
                  key={i}
                  className={`slot-dot ${i < filledSlots ? 'slot-dot--filled' : ''}`}
                />
              ))}
            </span>
            <span className="slot-counter__label">
              {filledSlots} of {maxImages} photos
              {isReady && <span className="slot-counter__ready"> · Ready</span>}
            </span>
          </div>

          {/* Photo grid */}
          <div className="photo-grid" role="list" aria-label="Selected photos">
            {images.map((file, index) => (
              <PhotoCard
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                index={index}
                isFirst={index === 0}
                onRemove={() => removeImage(index)}
                onMoveLeft={() => moveLeft(index)}
                totalCount={images.length}
              />
            ))}

            {/* Add more slot */}
            {canAddMore && (
              <button
                className="add-more-slot"
                onClick={openPicker}
                aria-label={`Add more photos (${maxImages - images.length} remaining)`}
              >
                <span className="add-more-icon" aria-hidden="true">+</span>
                <span className="add-more-label">Add photo</span>
              </button>
            )}
          </div>

          {/* Primary photo label */}
          <p className="primary-label" aria-live="polite">
            <span className="primary-dot" aria-hidden="true" />
            First photo is your cover image
          </p>
        </div>
      )}

      {/* ── Validation Error */}
      {validationError && (
        <div className="upload-error" role="alert">
          <span aria-hidden="true">⚠</span> {validationError}
        </div>
      )}

      {/* ── Tip (only after first photo added) */}
      {images.length === 1 && (
        <div className="uploader-tip">
          <span className="tip-icon" aria-hidden="true">💡</span>
          <span>You can generate with 1 photo, but we recommend at least 2 — front, back, label, and any wear — for best AI results.</span>
        </div>
      )}

      {/* ── Styles ── */}
      <style>{`
        .uploader { display: flex; flex-direction: column; gap: 16px; }

        /* ── Drop Zone ── */
        .drop-zone {
          border: 1.5px dashed var(--color-border-dashed);
          border-radius: var(--radius-xl);
          padding: 48px 24px; text-align: center; cursor: pointer;
          transition: border-color 0.2s, background 0.2s;
          -webkit-tap-highlight-color: transparent; user-select: none;
          background: var(--color-bg-card);
        }
        .drop-zone:hover, .drop-zone:focus-visible {
          border-color: var(--color-primary); background: var(--color-primary-light); outline: none;
        }
        .drop-zone:active { background: var(--color-primary-light); }
        .drop-zone--dragging { border-color: var(--color-primary); background: var(--color-primary-light); }

        .drop-zone__inner { display: flex; flex-direction: column; align-items: center; gap: 10px; pointer-events: none; }

        .drop-zone__icon-wrap {
          width: 52px; height: 52px; border-radius: 50%;
          background: var(--color-primary-light);
          display: flex; align-items: center; justify-content: center; margin-bottom: 4px;
        }
        .drop-zone__icon { font-size: 26px; position: relative; z-index: 1; }
        .drop-zone__icon-ring { display: none; }

        .drop-zone__title { font-size: 15px; font-weight: 500; color: var(--color-text-heading); }
        .drop-zone__subtitle { font-size: 13px; color: var(--color-text-hint); line-height: 1.6; }
        .drop-zone__format-hint { font-size: 11px; color: var(--color-text-hint); letter-spacing: 0.02em; margin-top: 4px; }

        /* ── Photo Section ── */
        .photo-section { display: flex; flex-direction: column; gap: 14px; }

        .slot-counter { display: flex; align-items: center; gap: 10px; }
        .slot-counter__dots { display: flex; gap: 5px; }
        .slot-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: var(--color-border); border: 1.5px solid var(--color-border-dashed);
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
        }
        .slot-dot--filled { background: var(--color-primary); border-color: var(--color-primary); transform: scale(1.15); }
        .slot-counter__label { font-size: 12px; color: var(--color-text-hint); font-weight: 400; }
        .slot-counter__ready { color: var(--success); font-weight: 500; }

        .photo-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }

        /* ── Add More Slot ── */
        .add-more-slot {
          aspect-ratio: 4 / 3; border: 1.5px dashed var(--color-border-dashed);
          border-radius: var(--radius-sm); display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 6px; cursor: pointer;
          background: transparent; transition: border-color 0.15s, background 0.15s;
          -webkit-tap-highlight-color: transparent;
        }
        .add-more-slot:hover, .add-more-slot:focus-visible {
          border-color: var(--color-primary); background: var(--color-primary-light); outline: none;
        }
        .add-more-icon { font-size: 22px; color: var(--color-text-hint); font-weight: 400; line-height: 1; }
        .add-more-label { font-size: 11px; color: var(--color-text-hint); font-weight: 400; }

        .primary-label { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--color-text-hint); }
        .primary-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--color-primary); flex-shrink: 0; }

        /* ── Validation Error ── */
        .upload-error {
          padding: 10px 14px;
          background: rgba(220,38,38,0.06); border: 0.5px solid rgba(220,38,38,0.2);
          border-radius: var(--radius-sm); font-size: 13px; color: #DC2626;
          display: flex; align-items: center; gap: 8px;
        }

        /* ── Tip Card ── */
        .uploader-tip {
          display: flex; align-items: flex-start; gap: 10px; padding: 14px 16px;
          background: var(--color-tip-bg); border: 0.5px solid var(--color-tip-border);
          border-radius: var(--radius-lg); font-size: 12px; color: var(--color-text-secondary); line-height: 1.6;
        }
        .tip-icon { flex-shrink: 0; font-size: 16px; color: var(--color-tip-icon); }

        /* ── Photo Card ── */
        .photo-card {
          position: relative; aspect-ratio: 4 / 3;
          border-radius: var(--radius-sm); overflow: hidden;
          background: var(--color-primary-light);
        }
        .photo-card--primary { border: 1.5px solid var(--color-primary); }
        .photo-card__img { width: 100%; height: 100%; object-fit: cover; display: block; }

        .photo-card__badge {
          position: absolute; top: 8px; left: 8px;
          background: var(--color-primary-light); color: var(--color-primary);
          font-size: 9px; font-weight: 500; letter-spacing: 0.06em; text-transform: uppercase;
          padding: 2px 7px; border-radius: 9999px; pointer-events: none;
        }

        .photo-card__actions {
          position: absolute; bottom: 0; left: 0; right: 0;
          display: flex; justify-content: space-between; padding: 6px;
          background: linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%);
        }
        .photo-card__btn {
          width: 30px; height: 30px; border-radius: 50%; border: none;
          display: flex; align-items: center; justify-content: center; font-size: 13px;
          cursor: pointer; transition: transform 0.1s, opacity 0.1s;
          -webkit-tap-highlight-color: transparent;
        }
        .photo-card__btn:active { transform: scale(0.85); }
        .photo-card__btn--move { background: rgba(255,255,255,0.2); color: #fff; }
        .photo-card__btn--move:disabled { opacity: 0; pointer-events: none; }
        .photo-card__btn--remove { background: rgba(220,38,38,0.85); color: #fff; }
      `}</style>
    </div>
  );
}

// ─── PhotoCard Sub-component ──────────────────────────────────────────────────
function PhotoCard({ file, index, isFirst, onRemove, onMoveLeft, totalCount }) {
  const [objectUrl] = useState(() => URL.createObjectURL(file));

  return (
    <div
      className={`photo-card ${isFirst ? 'photo-card--primary' : ''}`}
      role="listitem"
      aria-label={`Photo ${index + 1}${isFirst ? ', cover image' : ''}`}
    >
      <img
        className="photo-card__img"
        src={objectUrl}
        alt={`Listing photo ${index + 1}`}
        loading="lazy"
        draggable={false}
      />

      {/* Cover badge on first photo */}
      {isFirst && (
        <div className="photo-card__badge" aria-hidden="true">
          Cover
        </div>
      )}

      {/* Action overlay */}
      <div className="photo-card__actions">
        {/* Move left — make this photo the cover */}
        <button
          className="photo-card__btn photo-card__btn--move"
          onClick={onMoveLeft}
          disabled={isFirst}
          aria-label={isFirst ? 'Already cover image' : 'Set as cover image'}
          title={isFirst ? 'Already cover' : 'Set as cover'}
        >
          ←
        </button>

        {/* Remove */}
        <button
          className="photo-card__btn photo-card__btn--remove"
          onClick={onRemove}
          aria-label={`Remove photo ${index + 1}`}
          title="Remove photo"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
